/**
 * Policy Detail Page
 * Displays full policy details with actions based on status
 */

import { useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import {
  ArrowLeft,
  Edit,
  Rocket,
  Power,
  Trash2,
  RefreshCw,
  AlertCircle,
  ChevronDown,
  ChevronRight,
  Code,
  Sparkles,
  Pencil,
  Loader2,
} from 'lucide-react'

import {
  SidebarTrigger,
} from '@/components/ui/sidebar'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Separator } from '@/components/ui/separator'
import { Skeleton } from '@/components/ui/skeleton'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { PolicyXACMLViewer } from '@/components/policies/policy-xacml-viewer'
import { PolicyAttributesViewer } from '@/components/policies/policy-attributes-viewer'
import { PolicyRulesViewer } from '@/components/policies/policy-rules-viewer'
import {
  usePolicy,
  useDeployPolicy,
  useUndeployPolicy,
  useDeletePolicy,
  useGenerateSummary,
  useUpdateSummary,
} from '@/hooks/use-policies'
import type { PolicyDraftStatus } from '@/types/policy'
import { parseXacmlPolicy } from '@/lib/xacml-parser'
import { cn } from '@/lib/utils'

/**
 * Status badge variant mapping
 */
function getStatusBadgeVariant(
  status: PolicyDraftStatus
): 'default' | 'secondary' | 'destructive' | 'outline' {
  switch (status) {
    case 'ACTIVE':
      return 'default'
    case 'DRAFT':
      return 'secondary'
    case 'INACTIVE':
      return 'outline'
    default:
      return 'outline'
  }
}

/**
 * Format date for display
 */
function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/**
 * Loading skeleton for policy detail
 */
function PolicyDetailSkeleton() {
  return (
    <div className="space-y-6">
      {/* Header skeleton */}
      <div className="flex items-center justify-between">
        <div className="space-y-2">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-5 w-24" />
        </div>
        <div className="flex gap-2">
          <Skeleton className="h-9 w-20" />
          <Skeleton className="h-9 w-24" />
        </div>
      </div>

      {/* Info card skeleton */}
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-40" />
        </CardHeader>
        <CardContent className="space-y-4">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-4 w-1/2" />
        </CardContent>
      </Card>

      {/* XACML viewer skeleton */}
      <Skeleton className="h-64 w-full" />
    </div>
  )
}

/**
 * Policy Detail Page Component
 */
export function PolicyDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  // Fetch policy data
  const { data: policy, isLoading, isError, error, refetch } = usePolicy(id)

  // Mutations - hooks now take policyId as parameter
  const deployMutation = useDeployPolicy(id ?? '')
  const undeployMutation = useUndeployPolicy(id ?? '')
  const deleteMutation = useDeletePolicy()

  // Summary mutations
  const generateSummaryMutation = useGenerateSummary(id ?? '')
  const updateSummaryMutation = useUpdateSummary(id ?? '')

  // Dialog states
  const [showDeployDialog, setShowDeployDialog] = useState(false)
  const [showUndeployDialog, setShowUndeployDialog] = useState(false)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [undeployNotes, setUndeployNotes] = useState('')
  const [showAdvancedXacml, setShowAdvancedXacml] = useState(false)
  const [isEditingSummary, setIsEditingSummary] = useState(false)
  const [editedSummary, setEditedSummary] = useState('')

  // Parse XACML content for structured display
  const parsedPolicy = policy?.xacmlPolicy
    ? parseXacmlPolicy(policy.xacmlPolicy)
    : null

  // Handle deploy
  const handleDeploy = async () => {
    if (!id) return

    deployMutation.mutate(undefined, {
      onSuccess: () => {
        setShowDeployDialog(false)
      },
    })
  }

  // Handle undeploy
  const handleUndeploy = async () => {
    if (!id) return
    if (undeployNotes.length > 1000) return

    undeployMutation.mutate(
      { notes: undeployNotes.trim() },
      {
        onSuccess: () => {
          setShowUndeployDialog(false)
          setUndeployNotes('')
        },
      }
    )
  }

  // Handle delete
  const handleDelete = async () => {
    if (!id) return
    deleteMutation.mutate(id)
  }

  // Determine which actions are available based on status
  const canEdit = policy?.status === 'DRAFT' || policy?.status === 'INACTIVE'
  const canDeploy =
    policy?.status === 'DRAFT' || policy?.status === 'INACTIVE'
  const canUndeploy = policy?.status === 'ACTIVE'
  const canDelete = policy?.status !== 'ACTIVE'

  return (
    <>
      {/* Header */}
      <header className="flex h-12 shrink-0 items-center justify-between gap-2 border-b px-4 transition-[width,height] ease-linear group-has-[[data-collapsible=icon]]/sidebar-wrapper:h-12">
        <div className="flex items-center gap-2">
          <SidebarTrigger className="-ml-1" />
          <Separator orientation="vertical" className="mr-2 h-4" />
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/pac/policies')}
            className="gap-1"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </Button>
          <Separator orientation="vertical" className="mx-2 h-4" />
          <h1 className="text-base font-medium">Policy Details</h1>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => refetch()}
          disabled={isLoading}
          className="h-8"
        >
          <RefreshCw
            className={cn('mr-2 h-3.5 w-3.5', isLoading && 'animate-spin')}
          />
          Refresh
        </Button>
      </header>

      {/* Main Content */}
      <div className="flex flex-1 flex-col gap-4 p-4 md:gap-6 md:p-6">
        {/* Error State */}
        {isError && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription className="flex items-center justify-between">
              <span>
                Failed to load policy:{' '}
                {error instanceof Error ? error.message : 'Unknown error'}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => refetch()}
                className="ml-4"
              >
                Retry
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {/* Loading State */}
        {isLoading && <PolicyDetailSkeleton />}

        {/* Policy Content */}
        {policy && !isLoading && (
          <div className="space-y-6">
            {/* Policy Header */}
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div className="space-y-1">
                <div className="flex items-center gap-3">
                  <h2 className="text-2xl font-semibold">{policy.name}</h2>
                  <Badge variant={getStatusBadgeVariant(policy.status)}>
                    {policy.status}
                  </Badge>
                </div>
                {policy.description && (
                  <p className="text-muted-foreground">{policy.description}</p>
                )}
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground pt-1">
                  <span>Created by {policy.createdByUser?.email ?? `${policy.createdBy.slice(0, 8)}...`}</span>
                  <span>Created {formatDate(policy.createdAt)}</span>
                  <span>Updated {formatDate(policy.updatedAt)}</span>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-wrap gap-2">
                {canEdit && (
                  <Button variant="outline" size="sm" asChild>
                    <Link to={`/pac/policies/${id}/edit`}>
                      <Edit className="mr-2 h-4 w-4" />
                      Edit
                    </Link>
                  </Button>
                )}
                {canDeploy && (
                  <Button
                    size="sm"
                    onClick={() => setShowDeployDialog(true)}
                    disabled={deployMutation.isPending}
                  >
                    <Rocket className="mr-2 h-4 w-4" />
                    Deploy
                  </Button>
                )}
                {canUndeploy && (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setShowUndeployDialog(true)}
                    disabled={undeployMutation.isPending}
                  >
                    <Power className="mr-2 h-4 w-4" />
                    Undeploy
                  </Button>
                )}
                {canDelete && (
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => setShowDeleteDialog(true)}
                    disabled={deleteMutation.isPending}
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    Delete
                  </Button>
                )}
              </div>
            </div>

            {/* Policy Summary */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <div>
                  <CardTitle className="text-lg">Policy Summary</CardTitle>
                  <CardDescription>
                    Human-readable explanation of the policy
                  </CardDescription>
                </div>
                {!isEditingSummary && (
                  <div className="flex gap-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => {
                        setEditedSummary(policy.humanReadable ?? '')
                        setIsEditingSummary(true)
                      }}
                      title="Edit summary"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => generateSummaryMutation.mutate()}
                      disabled={generateSummaryMutation.isPending}
                    >
                      {generateSummaryMutation.isPending ? (
                        <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Sparkles className="mr-1.5 h-3.5 w-3.5" />
                      )}
                      Generate AI Summary
                    </Button>
                  </div>
                )}
              </CardHeader>
              <CardContent>
                {isEditingSummary ? (
                  <div className="space-y-3">
                    <Textarea
                      value={editedSummary}
                      onChange={(e) => setEditedSummary(e.target.value)}
                      placeholder="Enter a human-readable summary of this policy..."
                      rows={3}
                    />
                    <div className="flex gap-2 justify-end">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setIsEditingSummary(false)}
                      >
                        Cancel
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => {
                          updateSummaryMutation.mutate(editedSummary, {
                            onSuccess: () => setIsEditingSummary(false),
                          })
                        }}
                        disabled={updateSummaryMutation.isPending}
                      >
                        {updateSummaryMutation.isPending ? 'Saving...' : 'Save'}
                      </Button>
                    </div>
                  </div>
                ) : policy.humanReadable ? (
                  <p className="text-sm leading-relaxed whitespace-pre-wrap">
                    {policy.humanReadable}
                  </p>
                ) : (
                  <p className="text-sm text-muted-foreground italic">
                    No summary yet. Click &ldquo;Generate AI Summary&rdquo; to create one.
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Policy Target - Structured Attributes */}
            <PolicyAttributesViewer
              attributes={parsedPolicy?.target ?? { subject: [], resource: [], action: [], environment: [] }}
              title="Policy Target"
              description="Conditions that determine when this policy applies"
            />

            {/* Policy Rules */}
            <PolicyRulesViewer
              rules={parsedPolicy?.rules ?? []}
              title="Policy Rules"
            />

            {/* Advanced: Raw XACML (Collapsible) */}
            <Collapsible open={showAdvancedXacml} onOpenChange={setShowAdvancedXacml}>
              <CollapsibleTrigger asChild>
                <Button
                  variant="ghost"
                  className="w-full justify-between px-4 py-3 h-auto border rounded-lg hover:bg-muted/50"
                >
                  <div className="flex items-center gap-2">
                    <Code className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium">Advanced: Raw XACML</span>
                  </div>
                  {showAdvancedXacml ? (
                    <ChevronDown className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  )}
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="mt-2">
                  <PolicyXACMLViewer
                    xacmlContent={policy.xacmlPolicy}
                    maxHeight="500px"
                  />
                </div>
              </CollapsibleContent>
            </Collapsible>
          </div>
        )}
      </div>

      {/* Deploy Confirmation Dialog */}
      <AlertDialog open={showDeployDialog} onOpenChange={setShowDeployDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Deploy Policy</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to deploy this policy? Once deployed, it
              will be active and enforced.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeploy}
              disabled={deployMutation.isPending}
            >
              {deployMutation.isPending ? 'Deploying...' : 'Deploy'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Undeploy Dialog */}
      <AlertDialog
        open={showUndeployDialog}
        onOpenChange={setShowUndeployDialog}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Undeploy Policy</AlertDialogTitle>
            <AlertDialogDescription>
              This will undeploy the active policy. You can optionally provide a
              reason for undeployment.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-4">
            <Label htmlFor="undeploy-notes">Undeploy Notes (optional)</Label>
            <Textarea
              id="undeploy-notes"
              value={undeployNotes}
              onChange={(e) => setUndeployNotes(e.target.value)}
              placeholder="Enter reason for undeployment..."
              className="mt-2"
              rows={3}
              maxLength={1000}
            />
            <p className="mt-1 text-xs text-muted-foreground">
              {undeployNotes.length}/1000 characters
            </p>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setUndeployNotes('')}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleUndeploy}
              disabled={undeployMutation.isPending || undeployNotes.length > 1000}
            >
              {undeployMutation.isPending ? 'Undeploying...' : 'Undeploy'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Policy</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this policy? This action cannot
              be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleteMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}

export default PolicyDetailPage
